import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../lib/api'

interface RevenueDashboard {
  thisMonth: {
    revenue: number
    transactions: number
    avgOrderValue: number
    daysRemaining: number
  }
  byProduct: Array<{
    name: string
    revenue: number
    transactions: number
    percentOfTotal: number
  }>
  dailyRevenue: Array<{
    date: string
    revenue: number
  }>
  trafficSources: Array<{
    source: string
    visits: number
    conversions: number
    revenue: number
  }>
  upsellPerformance: Array<{
    offer: string
    shown: number
    accepted: number
    takeRate: number
    revenue: number
  }>
  aiRecommendations: Array<{
    id: string
    title: string
    content: string
    type: string
    createdAt: number
  }>
}

const GOAL = 1_000_000

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

function formatCurrencyDollars(dollars: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(dollars)
}

function formatPercent(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function AdminRevenue() {
  const [data, setData] = useState<RevenueDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const token = localStorage.getItem('fc_session_token')
    fetch(`${API_URL}/api/revenue/dashboard`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to load revenue data')
        return res.json()
      })
      .then(result => {
        setData(result.data || result)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F2EDE6',
      }}>
        <p style={{ color: '#555' }}>Loading revenue data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F2EDE6',
        padding: '2rem',
      }}>
        <p style={{ color: '#dc2626', marginBottom: '1.5rem' }}>{error}</p>
        <Link
          to="/dashboard"
          style={{
            padding: '0.875rem 2rem',
            background: '#0f172a',
            color: '#fff',
            borderRadius: '5px',
            textDecoration: 'none',
            fontSize: '0.95rem',
            fontWeight: 500,
          }}
        >
          Back to Dashboard
        </Link>
      </div>
    )
  }

  // Use fallback values if API returns partial data
  const thisMonth = data?.thisMonth || { revenue: 0, transactions: 0, avgOrderValue: 0, daysRemaining: 0 }
  const byProduct = data?.byProduct || []
  const dailyRevenue = data?.dailyRevenue || []
  const trafficSources = data?.trafficSources || []
  const upsellPerformance = data?.upsellPerformance || []
  const aiRecommendations = data?.aiRecommendations || []

  const revenueAmount = thisMonth.revenue / 100
  const progressPercent = Math.min((revenueAmount / (GOAL / 100)) * 100, 100)
  const maxDailyRevenue = dailyRevenue.length > 0
    ? Math.max(...dailyRevenue.map(d => d.revenue))
    : 1

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: '5px',
    padding: '1.5rem',
  }

  const tableHeaderStyle: React.CSSProperties = {
    textAlign: 'left',
    padding: '0.75rem 1rem',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#94a3b8',
    borderBottom: '1px solid #e2e8f0',
  }

  const tableCellStyle: React.CSSProperties = {
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    color: '#333',
    borderBottom: '1px solid #f1f5f9',
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#F2EDE6',
    }}>
      {/* Header */}
      <section style={{
        background: '#fff',
        borderBottom: '1px solid #e2e8f0',
        padding: '1.5rem',
      }}>
        <div style={{
          maxWidth: '1100px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem',
        }}>
          <div>
            <Link
              to="/dashboard"
              style={{
                color: '#94a3b8',
                textDecoration: 'none',
                fontSize: '0.825rem',
                fontWeight: 500,
              }}
            >
              &larr; Dashboard
            </Link>
            <h1 style={{
              fontSize: '1.5rem',
              fontWeight: 500,
              color: '#0f172a',
              marginTop: '0.25rem',
            }}>
              Revenue Dashboard
            </h1>
          </div>
          <div style={{
            fontSize: '0.825rem',
            color: '#94a3b8',
          }}>
            {thisMonth.daysRemaining} days remaining this month
          </div>
        </div>
      </section>

      <div style={{
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '2rem 1.5rem 4rem',
      }}>
        {/* Progress Toward Goal */}
        <div style={{
          ...cardStyle,
          marginBottom: '2rem',
          textAlign: 'center',
        }}>
          <div style={{
            fontSize: '0.8rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: '#94a3b8',
            marginBottom: '0.75rem',
          }}>
            Monthly Revenue Goal
          </div>
          <div style={{
            fontSize: '2.5rem',
            fontWeight: 600,
            color: '#0f172a',
            marginBottom: '0.5rem',
          }}>
            {formatCurrency(thisMonth.revenue)}
            <span style={{
              fontSize: '1.25rem',
              color: '#94a3b8',
              fontWeight: 400,
            }}>
              {' '}/ {formatCurrencyDollars(GOAL / 100)}
            </span>
          </div>
          <div style={{
            width: '100%',
            maxWidth: '600px',
            height: '12px',
            background: '#f1f5f9',
            borderRadius: '6px',
            margin: '0 auto',
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: progressPercent >= 100 ? '#16a34a' : '#dc2626',
              borderRadius: '6px',
              transition: 'width 0.5s ease',
            }} />
          </div>
          <div style={{
            fontSize: '0.825rem',
            color: '#94a3b8',
            marginTop: '0.5rem',
          }}>
            {progressPercent.toFixed(1)}% of goal
          </div>
        </div>

        {/* Stat Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}>
          {[
            { label: 'This Month Revenue', value: formatCurrency(thisMonth.revenue) },
            { label: 'Transactions', value: thisMonth.transactions.toLocaleString() },
            { label: 'Avg Order Value', value: formatCurrency(thisMonth.avgOrderValue) },
            { label: 'Days Remaining', value: thisMonth.daysRemaining.toString() },
          ].map((stat, i) => (
            <div key={i} style={cardStyle}>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: '#94a3b8',
                marginBottom: '0.5rem',
              }}>
                {stat.label}
              </div>
              <div style={{
                fontSize: '1.75rem',
                fontWeight: 600,
                color: '#0f172a',
              }}>
                {stat.value}
              </div>
            </div>
          ))}
        </div>

        {/* Revenue by Product */}
        {byProduct.length > 0 && (
          <div style={{ ...cardStyle, marginBottom: '2rem', overflow: 'auto' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 500,
              color: '#0f172a',
              marginBottom: '1rem',
            }}>
              Revenue by Product
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Product</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Revenue</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Transactions</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {byProduct.map((product, i) => (
                  <tr key={i}>
                    <td style={tableCellStyle}>{product.name}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 500 }}>
                      {formatCurrency(product.revenue)}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {product.transactions}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {formatPercent(product.percentOfTotal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Daily Revenue Chart */}
        {dailyRevenue.length > 0 && (
          <div style={{ ...cardStyle, marginBottom: '2rem' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 500,
              color: '#0f172a',
              marginBottom: '1.5rem',
            }}>
              Daily Revenue
            </h3>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '4px',
              height: '160px',
              padding: '0 0 1.5rem',
              overflow: 'auto',
            }}>
              {dailyRevenue.map((day, i) => {
                const height = maxDailyRevenue > 0
                  ? Math.max((day.revenue / maxDailyRevenue) * 140, 4)
                  : 4
                return (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      flex: '1 0 24px',
                      minWidth: '24px',
                    }}
                  >
                    <div
                      style={{
                        width: '100%',
                        maxWidth: '32px',
                        height: `${height}px`,
                        background: day.revenue > 0 ? '#dc2626' : '#f1f5f9',
                        borderRadius: '3px 3px 0 0',
                        transition: 'height 0.3s ease',
                      }}
                      title={`${formatDate(day.date)}: ${formatCurrency(day.revenue)}`}
                    />
                    <div style={{
                      fontSize: '0.6rem',
                      color: '#94a3b8',
                      marginTop: '0.375rem',
                      whiteSpace: 'nowrap',
                    }}>
                      {formatDate(day.date)}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Traffic Sources */}
        {trafficSources.length > 0 && (
          <div style={{ ...cardStyle, marginBottom: '2rem', overflow: 'auto' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 500,
              color: '#0f172a',
              marginBottom: '1rem',
            }}>
              Top Traffic Sources
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Source</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Visits</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Conversions</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {trafficSources.map((source, i) => (
                  <tr key={i}>
                    <td style={tableCellStyle}>{source.source}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {source.visits.toLocaleString()}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {source.conversions}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 500 }}>
                      {formatCurrency(source.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Upsell Performance */}
        {upsellPerformance.length > 0 && (
          <div style={{ ...cardStyle, marginBottom: '2rem', overflow: 'auto' }}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 500,
              color: '#0f172a',
              marginBottom: '1rem',
            }}>
              Upsell Performance
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Offer</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Shown</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Accepted</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Take Rate</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Revenue</th>
                </tr>
              </thead>
              <tbody>
                {upsellPerformance.map((upsell, i) => (
                  <tr key={i}>
                    <td style={tableCellStyle}>{upsell.offer}</td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {upsell.shown}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {upsell.accepted}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      {formatPercent(upsell.takeRate)}
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 500 }}>
                      {formatCurrency(upsell.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* AI Recommendations */}
        {aiRecommendations.length > 0 && (
          <div style={cardStyle}>
            <h3 style={{
              fontSize: '1rem',
              fontWeight: 500,
              color: '#0f172a',
              marginBottom: '1rem',
            }}>
              Revenue Optimization Insights
            </h3>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}>
              {aiRecommendations.map((rec) => (
                <div
                  key={rec.id}
                  style={{
                    background: '#faf9f7',
                    border: '1px solid #e2e8f0',
                    borderRadius: '5px',
                    padding: '1.25rem',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                  }}>
                    <h4 style={{
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      color: '#0f172a',
                      margin: 0,
                    }}>
                      {rec.title}
                    </h4>
                    <span style={{
                      fontSize: '0.7rem',
                      color: '#94a3b8',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      background: '#f1f5f9',
                      padding: '0.2rem 0.5rem',
                      borderRadius: '3px',
                    }}>
                      {rec.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#555',
                    lineHeight: 1.6,
                    margin: 0,
                  }}>
                    {rec.content}
                  </p>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#94a3b8',
                    marginTop: '0.5rem',
                  }}>
                    {new Date(rec.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty state for when there's no data */}
        {!byProduct.length && !dailyRevenue.length && !trafficSources.length && !upsellPerformance.length && !aiRecommendations.length && (
          <div style={{
            ...cardStyle,
            textAlign: 'center',
            padding: '3rem 2rem',
          }}>
            <p style={{
              color: '#555',
              fontSize: '0.95rem',
              marginBottom: '1rem',
            }}>
              Revenue data will appear here as transactions come in.
            </p>
            <Link
              to="/dashboard"
              style={{
                color: '#dc2626',
                textDecoration: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
              }}
            >
              &larr; Back to Dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
